import React from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Iconify from 'src/components/Iconify';
import MetricCard from './components/MetricCard';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const PRIMARY = '#1877F2';
const SECONDARY = '#42A5F5';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function truncate(str, len = 80) {
  if (!str) return '—';
  return str.length > len ? `${str.slice(0, len)}...` : str;
}

export default function FacebookTab({ data }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-[#1877F2] rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Carregando dados do Facebook...</p>
        </div>
      </div>
    );
  }

  const { summary, daily, demographics, top_posts, page_fans_by_day } = data;

  const dailyFormatted = (daily || []).map((row) => ({
    ...row,
    dateLabel: formatDate(row.date),
  }));

  const fansFormatted = (page_fans_by_day || []).map((row) => ({
    ...row,
    dateLabel: formatDate(row.date),
  }));

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <MetricCard
          icon="eva:eye-outline"
          label="Page Views"
          value={formatNumber(summary?.page_views)}
          growth={summary?.page_views_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:activity-outline"
          label="Impressions"
          value={formatNumber(summary?.page_impressions)}
          growth={summary?.page_impressions_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:heart-outline"
          label="Engajamentos"
          value={formatNumber(summary?.post_engagements)}
          growth={summary?.post_engagements_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:people-outline"
          label="Page Fans"
          value={formatNumber(summary?.page_fans)}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:person-add-outline"
          label="Novos Fans"
          value={formatNumber(summary?.page_fans_add)}
          growth={summary?.page_fans_add_growth_pct}
          color={PRIMARY}
        />
      </div>

      {/* Impressions Chart */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:trending-up-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          Impressions ao Longo do Tempo
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="page_impressions"
              name="Impressions"
              stroke={PRIMARY}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="page_impressions_unique"
              name="Alcance (Unique)"
              stroke={SECONDARY}
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Page Fans Evolution */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:people-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          Evolução de Page Fans
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={fansFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="fans"
              name="Fans"
              stroke={PRIMARY}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5 }}
              fill={PRIMARY}
              fillOpacity={0.1}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Post Engagement Breakdown */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:heart-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          Engajamento por Dia
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="reactions" name="Reações" fill={PRIMARY} radius={[3, 3, 0, 0]} />
            <Bar dataKey="comments" name="Comentários" fill={SECONDARY} radius={[3, 3, 0, 0]} />
            <Bar dataKey="shares" name="Compartilhamentos" fill="#90CAF9" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Demographics */}
      <div className={d.charts.grid2}>
        {/* Age & Gender */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>
            <Iconify icon="eva:bar-chart-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
            Demografia por Idade e Gênero
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={demographics?.age_gender || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="range" type="category" tick={{ fontSize: 11 }} width={60} />
              <Tooltip />
              <Legend />
              <Bar dataKey="male" name="Masculino" fill={PRIMARY} radius={[0, 3, 3, 0]} />
              <Bar dataKey="female" name="Feminino" fill="#E91E63" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Cities */}
        <div className={d.table.container}>
          <div className={d.table.header}>
            <h3 className={d.table.headerTitle}>
              <Iconify icon="eva:pin-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
              Top Cidades
            </h3>
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            {(demographics?.cities || []).map((city, idx) => (
              <div key={idx} className={d.table.row}>
                <span className="text-xs font-medium text-gray-400 w-6">{idx + 1}</span>
                <span className={`${d.table.cell} flex-1`}>{city.name}</span>
                <span className="text-sm font-semibold text-gray-900">{formatNumber(city.count)}</span>
              </div>
            ))}
            {(!demographics?.cities || demographics.cities.length === 0) && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Sem dados de cidades</div>
            )}
          </div>
        </div>
      </div>

      {/* Top Posts Table */}
      <div className={d.table.container}>
        <div className={d.table.header}>
          <h3 className={d.table.headerTitle}>
            <Iconify icon="eva:file-text-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
            Top Posts
          </h3>
          <span className={d.table.cellMuted}>{(top_posts || []).length} posts</span>
        </div>

        {/* Table Header */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center bg-gray-50/60">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-1">Mensagem</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">Impressions</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">Reações</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">Comentários</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">Compartilh.</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">Cliques</span>
        </div>

        {/* Table Rows */}
        <div className="max-h-[400px] overflow-y-auto">
          {(top_posts || []).map((post, idx) => (
            <div key={post.external_id || idx} className={`${d.table.row} gap-2`}>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                {post.thumbnail ? (
                  <img
                    src={post.thumbnail}
                    alt=""
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Iconify icon="eva:image-outline" width={18} className="text-blue-300" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{truncate(post.message)}</p>
                  <p className={d.table.cellMuted}>
                    {post.type} &middot; {post.published_at ? new Date(post.published_at).toLocaleDateString('pt-BR') : '—'}
                  </p>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-900 w-24 text-right">{formatNumber(post.impressions)}</span>
              <span className="text-sm text-gray-700 w-20 text-right">{formatNumber(post.reactions)}</span>
              <span className="text-sm text-gray-700 w-24 text-right">{formatNumber(post.comments)}</span>
              <span className="text-sm text-gray-700 w-24 text-right">{formatNumber(post.shares)}</span>
              <span className="text-sm text-gray-700 w-20 text-right">{formatNumber(post.clicks)}</span>
            </div>
          ))}
          {(!top_posts || top_posts.length === 0) && (
            <div className="px-5 py-12 text-center">
              <Iconify icon="eva:file-text-outline" width={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Nenhum post encontrado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
